import {
   Alert,
   Box,
   type ButtonProps,
   Collapse,
   Flex,
   HStack,
   IconButton,
   Input,
   Link,
   Text,
   VStack,
} from '@chakra-ui/react';
import { faCircleExclamation, faMinus, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { multiplyMoney } from '@ifixit/helpers';
import { FaIcon } from '@ifixit/icons';
import type { CartLineItem as LineItem } from '@ifixit/shopify-cart-sdk';
import { useUpdateLineItemQuantity } from '@ifixit/shopify-cart-sdk/hooks';
import type { ElementType } from 'react';
import { useEffect, useState, useCallback, type ChangeEvent, type KeyboardEvent } from 'react';
import { ProductVariantPrice } from '../../commerce';
import type { CartImageComponent } from '../types';
import { CartLineItemImage } from './CartLineItemImage';
import type { LocaleCode } from '@ifixit/i18n';

interface CartLineItemProps {
   ImageComponent: CartImageComponent;
   lineItem: LineItem;
   onRemove: (lineItem: LineItem) => void;
   removeDisabled: boolean;
   linkComponent?: ElementType;
   localeCode: LocaleCode;
   ariaDecrease: string;
   ariaIncrease: string;
   notEnoughInventory: string;
   outOfStock: string;
   unableToUpdateQuantity: string;
}

export function CartLineItem({
   ImageComponent,
   lineItem,
   onRemove,
   removeDisabled,
   linkComponent,
   localeCode,
   ariaDecrease,
   ariaIncrease,
   notEnoughInventory,
   outOfStock,
   unableToUpdateQuantity,
}: CartLineItemProps) {
   const { updateLineItemQuantity, enabled: updateLineItemQuantityEnabled } =
      useUpdateLineItemQuantity();

   const [quantityInputValue, setQuantityInputValue] = useState<number | null>(lineItem.quantity);

   useEffect(() => {
      setQuantityInputValue(lineItem.quantity);
   }, [lineItem.quantity]);

   useEffect(() => {
      if (updateLineItemQuantity.isError) {
         const id = setTimeout(() => {
            updateLineItemQuantity.reset();
         }, 3000);
         return () => clearTimeout(id);
      }
   }, [updateLineItemQuantity.isError, updateLineItemQuantity.reset]);

   const currentQuantity = quantityInputValue ?? lineItem.quantity;

   const incrementQuantity = () => {
      setQuantityInputValue(currentQuantity + 1);
      updateLineItemQuantity.mutate({
         line: lineItem,
         quantityDelta: 1,
         analytics: { localeCode },
      });
   };

   const decrementQuantity = () => {
      if (currentQuantity <= 1) return;
      setQuantityInputValue(currentQuantity - 1);
      updateLineItemQuantity.mutate({
         line: lineItem,
         quantityDelta: -1,
         analytics: { localeCode },
      });
   };

   const handleQuantityChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '') {
         setQuantityInputValue(null);
      } else if (/^\d+$/.test(value)) {
         setQuantityInputValue(Number.parseInt(value, 10));
      }
   }, []);

   const submitQuantityChange = useCallback(() => {
      if (quantityInputValue === null || quantityInputValue < 1) {
         setQuantityInputValue(lineItem.quantity);
         return;
      }
      const delta = quantityInputValue - lineItem.quantity;
      if (delta !== 0) {
         updateLineItemQuantity.mutate({
            line: lineItem,
            quantityDelta: delta,
            analytics: { localeCode },
         });
      }
   }, [quantityInputValue, lineItem, updateLineItemQuantity, localeCode]);

   const handleQuantityKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         e.currentTarget.blur();
      }
   }, []);

   const quantityButtonProps: ButtonProps = {
      variant: 'solid',
      bgColor: 'gray.200',
      size: 'xxs',
      boxSize: '20px',
      _hover: { opacity: 0.8 },
   };

   return (
      <Flex
         direction="column"
         w="full"
         p="3"
         bgColor="white"
         data-testid={`cart-drawer-line-item-${lineItem.itemcode}`}
      >
         <Flex w="full" justify="space-between" align="stretch">
            <HStack spacing="3" align="flex-start">
               <CartLineItemImage
                  ImageComponent={ImageComponent}
                  src={lineItem.imageSrc}
                  alt={lineItem.name}
               />
               <Box>
                  <VStack align="flex-start">
                     <Flex direction="column">
                        <Link
                           as={linkComponent}
                           href={lineItem.url}
                           isExternal
                           fontWeight="semibold"
                           borderRadius="sm"
                           overflowWrap="break-word"
                        >
                           {lineItem.name}
                        </Link>
                        <Text color="gray.600" mt={1} mb={0} fontFamily="mono">
                           {lineItem.itemcode}
                        </Text>
                        <Text color="gray.600" my={2}>
                           {lineItem.variantTitle}
                        </Text>
                     </Flex>
                     <HStack gap={3}>
                        <IconButton
                           aria-label={ariaDecrease}
                           icon={<FaIcon icon={faMinus} h="4" color="gray.500" />}
                           isDisabled={!updateLineItemQuantityEnabled || currentQuantity <= 1}
                           onClick={decrementQuantity}
                           data-testid="cart-drawer-decrease-quantity"
                           {...quantityButtonProps}
                        />
                        <Input
                           value={quantityInputValue ?? ''}
                           onChange={handleQuantityChange}
                           onBlur={submitQuantityChange}
                           onKeyDown={handleQuantityKeyDown}
                           isDisabled={!updateLineItemQuantityEnabled}
                           color="gray.800"
                           fontSize="12px"
                           textAlign="center"
                           variant="unstyled"
                           w="24px"
                           p="0"
                           min={1}
                           data-testid="cart-drawer-quantity"
                           sx={{ fontVariantNumeric: 'tabular-nums' }}
                        />
                        <IconButton
                           aria-label={ariaIncrease}
                           icon={<FaIcon icon={faPlus} h="4" color="gray.500" />}
                           isDisabled={
                              !updateLineItemQuantityEnabled ||
                              (lineItem.quantityAvailable != null &&
                                 currentQuantity >= lineItem.quantityAvailable &&
                                 !lineItem.isBackordered &&
                                 !lineItem.isPreorder)
                           }
                           onClick={incrementQuantity}
                           data-testid="cart-drawer-increase-quantity"
                           {...quantityButtonProps}
                        />
                     </HStack>
                     <InventoryTooLowMessage
                        item={lineItem}
                        notEnoughInventory={notEnoughInventory}
                        outOfStock={outOfStock}
                     />
                     <Collapse in={updateLineItemQuantity.isError} animateOpacity>
                        <Alert status="error" bg="transparent" textColor="red.500" py="0" px="1">
                           <FaIcon icon={faCircleExclamation} h="4" mr="1.5" color="red.500" />
                           {unableToUpdateQuantity}
                        </Alert>
                     </Collapse>
                  </VStack>
               </Box>
            </HStack>
            <Flex direction="column" align="flex-end" justify="space-between">
               <IconButton
                  variant="ghost"
                  _hover={{ bg: 'gray.100', color: 'gray.400' }}
                  aria-label={`Remove ${lineItem.name} from cart`}
                  _active={{ bg: 'gray.200' }}
                  color="gray.300"
                  icon={<FaIcon icon={faTrash} boxSize="4" />}
                  size="xs"
                  maxW="24px"
                  isDisabled={removeDisabled}
                  onClick={() => onRemove(lineItem)}
                  data-testid="cart-drawer-remove-item"
               />
               <ProductVariantPrice
                  price={multiplyMoney(lineItem.price, lineItem.quantity)}
                  compareAtPrice={
                     lineItem.compareAtPrice == null
                        ? null
                        : multiplyMoney(lineItem.compareAtPrice, lineItem.quantity)
                  }
                  direction="column-reverse"
                  size="small"
                  localeCode={localeCode}
               />
            </Flex>
         </Flex>
      </Flex>
   );
}

function InventoryTooLowMessage({
   item,
   notEnoughInventory,
   outOfStock,
}: { item: LineItem; notEnoughInventory: string; outOfStock: string }) {
   if (
      item.quantityAvailable == null ||
      item.quantity <= item.quantityAvailable ||
      item.isPreorder ||
      item.isBackordered
   ) {
      return null;
   }
   const message = item.quantityAvailable < 1 ? outOfStock : notEnoughInventory;
   return (
      <Text fontSize="sm" textColor="red.500">
         {message}
      </Text>
   );
}
